import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import { useInputAmount } from '@/hooks/state/useInputAmount';
import { cn } from '@/lib/utils';
import { useSignalStore } from '@/page/token/signal/store';
import { useAppStore } from '@/store';
import { useBoomWallet } from '@boomeme/wallet-sdk';
import BigNumber from 'bignumber.js';
import { X } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

interface IProps {
  setOpen: (open: boolean) => void;
}

export default (props: IProps) => {
  const { setOpen } = props;
  const { chain } = useBoomWallet();
  const { fwClass, chainInfo } = useAppStore();
  const { t } = useTranslation();
  const { bscSignalTradingSetting, solSignalTradingSetting, setBscSignalTradingSetting, setSolSignalTradingSetting } = useSignalStore();
  const targetChainTradingSetting = chain == 'bsc' ? bscSignalTradingSetting : solSignalTradingSetting;
  const [openAlert, setOpenAlert] = useState(targetChainTradingSetting.alert);
  const [openMev, setOpenMev] = useState(targetChainTradingSetting.mev);
  const [openMerge, setOpenMerge] = useState(targetChainTradingSetting.merge);
  const [slippageOpen, setSlippageOpen] = useState(targetChainTradingSetting.slippageAuto);
  const [feeAutoOpen, setFeeAutoOpen] = useState(targetChainTradingSetting.feeAuto);

  const {
    inputAmount: slippage,
    onBlurInputAmount: onBlurInputSlippage,
    onChangeInputAmount: onChangeInputSlippage,
  } = useInputAmount(targetChainTradingSetting.slippage);

  const {
    inputAmount: fee,
    onBlurInputAmount: onBlurInputFee,
    onChangeInputAmount: onChangeInputFee,
  } = useInputAmount(targetChainTradingSetting.fee);

  const {
    inputAmount: quickBuy_0,
    onBlurInputAmount: onBlurInputQuickBuy_0,
    onChangeInputAmount: onChangeInputQuickBuy_0,
  } = useInputAmount(targetChainTradingSetting.quickBuys[0]);

  const {
    inputAmount: quickBuy_1,
    onBlurInputAmount: onBlurInputQuickBuy_1,
    onChangeInputAmount: onChangeInputQuickBuy_1,
  } = useInputAmount(targetChainTradingSetting.quickBuys[1]);

  const {
    inputAmount: quickBuy_2,
    onBlurInputAmount: onBlurInputQuickBuy_2,
    onChangeInputAmount: onChangeInputQuickBuy_2,
  } = useInputAmount(targetChainTradingSetting.quickBuys[2]);

  const {
    inputAmount: quickSell_0,
    onBlurInputAmount: onBlurInputQuickSell_0,
    onChangeInputAmount: onChangeInputQuickSell_0,
  } = useInputAmount(targetChainTradingSetting.quickSells[0]);

  const {
    inputAmount: quickSell_1,
    onBlurInputAmount: onBlurInputQuickSell_1,
    onChangeInputAmount: onChangeInputQuickSell_1,
  } = useInputAmount(targetChainTradingSetting.quickSells[1]);

  const {
    inputAmount: quickSell_2,
    onBlurInputAmount: onBlurInputQuickSell_2,
    onChangeInputAmount: onChangeInputQuickSell_2,
  } = useInputAmount(targetChainTradingSetting.quickSells[2]);

  const onConfirm = () => {
    const updatedSettings = {
      slippageAuto: slippageOpen,
      slippage,
      merge: openMerge,
      mev: openMev,
      feeAuto: feeAutoOpen,
      fee: fee,
      alert: openAlert,
      quickBuys: [quickBuy_0, quickBuy_1, quickBuy_2],
      quickSells: [quickSell_0, quickSell_1, quickSell_2],
    };
    if (chain == 'bsc') {
      setBscSignalTradingSetting(updatedSettings);
    } else {
      setSolSignalTradingSetting(updatedSettings);
    }
    setOpen(false);
  };

  const SlippageTips = useMemo(() => {
    if (slippageOpen) return () => null;
    const slippageObj = BigNumber(slippage);
    if (slippageObj.eq(0)) return () => <p>{t('Trade.slippageZeroTips')}</p>;
    if (slippageObj.lt(0.1)) return () => <p>{t('Trade.slippageLowTips')}</p>;
    if (slippageObj.gt(80)) return () => <p>{t('Trade.slippageExceedTips')}</p>;
    return () => null;
  }, [slippage, slippageOpen]);

  const FeeTips = useMemo(() => {
    if (feeAutoOpen) return () => null;
    const feeObj = BigNumber(fee);
    if (chainInfo.chain == 'solana') {
      if (feeObj.lt(0.012)) return () => <p>{t('Trade.solPriorityFeeLowTips')}</p>;
      if (feeObj.gt(2)) return () => <p>{t('Trade.solPriorityFeeExceedTips')}</p>;
    }
    if (chainInfo.chain == 'bsc') {
      if (feeObj.lt(0.1)) return () => <p>{t('Trade.bscGasFeeLowTips')}</p>;
      if (feeObj.gt(20)) return () => <p>{t('Trade.bscGasFeeExceedTips')}</p>;
    }
    return () => null;
  }, [fee, feeAutoOpen]);

  const [disabled, setDisabled] = useState(false);

  const isSlippageValid = (slippage: string) => {
    if (slippage == '') return false;
    const slippageObj = BigNumber(slippage);
    return !(slippageObj.lt(0.1) || slippageObj.eq(0) || slippageObj.gt(80));
  };

  const isFeeValid = (fee: string) => {
    if (fee == '') return false;
    const feeObj = BigNumber(fee);
    if (chainInfo.chain == 'solana') {
      return !(feeObj.lt(0.012) || feeObj.gt(2));
    }
    if (chainInfo.chain == 'bsc') {
      return !(feeObj.lt(0.1) || feeObj.gt(20));
    }
  };

  const isAnyGt100Percent = (quickSellList: string[]) => {
    if (quickSellList.length < 3) return false;
    return quickSellList.some((item) => {
      return BigNumber(item).gt(100) || BigNumber(item).eq(0) || item == '';
    });
  };

  useEffect(() => {
    const quickSellCheck = isAnyGt100Percent([quickSell_0, quickSell_1, quickSell_2]);
    setDisabled(quickSellCheck);
  }, [quickSell_0, quickSell_1, quickSell_2]);

  useEffect(() => {
    const shouldDisable = () => {
      if (slippageOpen && feeAutoOpen) {
        return false;
      }

      const slippageCheck = slippageOpen || isSlippageValid(slippage);
      const priorityCheck = feeAutoOpen || isFeeValid(fee);
      return !(slippageCheck && priorityCheck);
    };

    setDisabled(shouldDisable());
  }, [slippageOpen, slippage, feeAutoOpen, fee]);

  return (
    <>
      <div className="flex flex-col gap-y-[20px] text-[12px] mb-[100px]">
        <div className="flex justify-between">
          <span className={cn(fwClass, 'text-[24px]')}>{t('Signal.setting')}</span>
          <X className="size-[16px]" onClick={() => setOpen(false)} />
        </div>
        <div className="flex justify-between items-center">
          <span>{t('Signal.alert')}</span>
          <Switch
            dotClass="dark:bg-[#fff]"
            className="dark:data-[state=checked]:bg-buyColor data-[state=checked]:bg-buyColor"
            checked={openAlert}
            onCheckedChange={setOpenAlert}
          />
        </div>
        <div className="flex justify-between items-center">
          <span>{t('Signal.mergeSignals')}</span>
          <Switch
            dotClass="dark:bg-[#fff]"
            className="dark:data-[state=checked]:bg-buyColor data-[state=checked]:bg-buyColor"
            checked={openMerge}
            onCheckedChange={setOpenMerge}
          />
        </div>
        <div className="flex justify-between items-center">
          <span>{t('Signal.mevProtection')}</span>
          <Checkbox className="size-[20px] rounded-[5px]" checked={openMev} onCheckedChange={(e: boolean) => setOpenMev(e)} />
        </div>
        <div>
          <div className="flex justify-between items-center">
            <span>{t('Signal.slippage')}</span>
            <div className="flex items-center justify-between gap-x-[6px]">
              <Button
                variant="outline"
                className={cn('w-[104px] h-[36px] rounded-[8px]', slippageOpen && '!border-themeColor !border-[1px]')}
                onClick={() => setSlippageOpen(true)}
              >
                {t('Signal.auto')}
              </Button>
              <div
                className={cn(
                  'bg-[#F4F4F5] dark:bg-cardBgColor border-[1px] border-borderColor w-[128px] h-[36px] px-[12px] rounded-[8px] flex items-center',
                  !slippageOpen && 'border-themeColor border-[1px]',
                )}
                onClick={() => setSlippageOpen(false)}
              >
                <Input
                  className="h-full border-none outline-none text-[14px] px-0 shadow-none "
                  placeholder="Custom"
                  inputMode="decimal"
                  value={slippage}
                  onChange={onChangeInputSlippage}
                  onBlur={onBlurInputSlippage}
                />
                <span>%</span>
              </div>
            </div>
          </div>
          <div className="text-[#F0B90B] text-[12px] mt-[5px]">
            <SlippageTips />
          </div>
        </div>
        <div>
          <div className="flex justify-between items-center">
            <span>{chainInfo.chain == 'solana' ? t('Signal.priorityFee') : t('Signal.gasFee')}</span>
            <div className="flex items-center justify-between gap-x-[6px]">
              <Button
                variant="outline"
                className={cn('w-[104px] h-[36px] rounded-[8px]', feeAutoOpen && '!border-themeColor !border-[1px]')}
                onClick={() => setFeeAutoOpen(true)}
              >
                {t('Signal.auto')}
              </Button>
              <div
                className={cn(
                  'bg-[#F4F4F5] dark:bg-cardBgColor border-[1px] border-borderColor w-[128px] h-[36px] px-[12px] rounded-[8px] flex items-center',
                  !feeAutoOpen && '!border-themeColor !border-[1px]',
                )}
                onClick={() => setFeeAutoOpen(false)}
              >
                <Input
                  className="h-full border-none outline-none text-[14px] px-0 shadow-none"
                  placeholder="Custom"
                  inputMode="decimal"
                  value={fee}
                  onChange={onChangeInputFee}
                  onBlur={onBlurInputFee}
                />
                <span>{chainInfo.chain == 'solana' ? chainInfo.symbol : 'Gwei'}</span>
              </div>
            </div>
          </div>
          <div className="text-[#F0B90B] text-[12px] mt-[5px]">
            <FeeTips />
          </div>
        </div>
        <div className="flex flex-col gap-y-[6px]">
          <span className="">{t('Signal.amountQuickBuy', { symbol: chainInfo.symbol })}</span>
          <div className="flex gap-[10px]">
            <Input
              inputMode="decimal"
              className="text-center bg-[#F4F4F5] dark:bg-[#27272A] shadow-none outline-none border-none"
              placeholder="0.1"
              value={quickBuy_0}
              onChange={onChangeInputQuickBuy_0}
              onBlur={onBlurInputQuickBuy_0}
            />
            <Input
              inputMode="decimal"
              className="text-center bg-[#F4F4F5] dark:bg-[#27272A] shadow-none outline-none border-none"
              placeholder="0.5"
              value={quickBuy_1}
              onChange={onChangeInputQuickBuy_1}
              onBlur={onBlurInputQuickBuy_1}
            />
            <Input
              inputMode="decimal"
              className="text-center bg-[#F4F4F5] dark:bg-[#27272A] shadow-none outline-none border-none"
              placeholder="1"
              value={quickBuy_2}
              onChange={onChangeInputQuickBuy_2}
              onBlur={onBlurInputQuickBuy_2}
            />
          </div>
        </div>
        <div className="flex flex-col gap-y-[6px]">
          <span className="">{t('Signal.percentQuickSell')}</span>
          <div className="flex gap-[10px]">
            <div className="flex items-center bg-[#F4F4F5] dark:bg-[#27272A] rounded-[6px] px-[12px]">
              <Input
                className="text-center bg-transparent px-0 shadow-none outline-none border-none"
                placeholder="25"
                value={quickSell_0}
                onChange={onChangeInputQuickSell_0}
                onBlur={onBlurInputQuickSell_0}
              />
              <span>%</span>
            </div>
            <div className="flex items-center bg-[#F4F4F5] dark:bg-[#27272A] rounded-[6px] px-[12px]">
              <Input
                className="text-center bg-transparent px-0 shadow-none outline-none border-none"
                placeholder="50"
                value={quickSell_1}
                onChange={onChangeInputQuickSell_1}
                onBlur={onBlurInputQuickSell_1}
              />
              <span>%</span>
            </div>
            <div className="flex items-center bg-[#F4F4F5] dark:bg-[#27272A] rounded-[6px] px-[12px]">
              <Input
                className="text-center bg-transparent px-0 shadow-none outline-none border-none"
                placeholder="100"
                value={quickSell_2}
                onChange={onChangeInputQuickSell_2}
                onBlur={onBlurInputQuickSell_2}
              />
              <span>%</span>
            </div>
          </div>
        </div>
      </div>
      <div className="flex justify-between gap-x-[10px]">
        <Button variant="secondary" className={cn('w-full rounded-[12px] h-[48px] text-[14px]', fwClass)} onClick={() => setOpen(false)}>
          {t('Signal.cancel')}
        </Button>
        <Button
          className={cn('!bg-themeColor w-full rounded-[12px] h-[48px] text-[14px] text-black', fwClass)}
          disabled={disabled}
          onClick={onConfirm}
        >
          {t('Signal.confirm')}
        </Button>
      </div>
    </>
  );
};
